import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react'
import { Source, Destination, Connection } from '@mediaconnect-router/shared'

// State interface based on design document
interface RoutingState {
  sources: Source[]
  destinations: Destination[]
  connections: Connection[]
  loading: boolean
  error: string | null
}

interface UIState {
  selectedCrosspoint: { sourceId: string; destinationId: string } | null
  hoveredCrosspoint: { sourceId: string; destinationId: string } | null
  viewMode: '2d' | '3d'
  performance: {
    frameRate: number
    responseTime: number
  }
  /** Tracks whether the Shift key is currently held. Used to route clicks
   * into salvo-build selection instead of actual routing changes. */
  shiftHeld: boolean
  /** Ordered list of crosspoints the user has shift-clicked for salvo build. */
  salvoSelection: Array<{ sourceId: string; destinationId: string }>
  /** Per-user 10s undo buffer. Oldest entries at index 0, newest at the end.
   * Entries are auto-pruned after `expiresAt` and removed once executed. */
  undoBuffer: UndoEntry[]
}

/** Declarative inverse action. The App.tsx 'undo executor' interprets this
 * and dispatches through the same REST / WebSocket path that created the
 * original change, so there is no divergence between original and undo
 * semantics. */
export type UndoInverse =
  | { kind: 'connection'; sourceId: string; destinationId: string; action: 'connect' | 'disconnect' }
  | { kind: 'salvo-fire'; crosspoints: Array<{ sourceId: string; destinationId: string }> }

export interface UndoEntry {
  id: string
  description: string
  createdAt: number
  expiresAt: number
  inverse: UndoInverse
}

/** Window in ms that a routing change remains undoable. */
export const UNDO_WINDOW_MS = 10_000

interface ConnectionState {
  status: 'connected' | 'disconnected' | 'connecting'
  lastUpdate: Date | null
  retryCount: number
}

interface AppState {
  routing: RoutingState
  ui: UIState
  connection: ConnectionState
}

// Action types
type RoutingAction =
  | { type: 'SET_SOURCES'; payload: Source[] }
  | { type: 'SET_DESTINATIONS'; payload: Destination[] }
  | { type: 'SET_CONNECTIONS'; payload: Connection[] }
  | { type: 'ADD_CONNECTION'; payload: Connection }
  | { type: 'UPDATE_CONNECTION'; payload: Connection }
  | { type: 'REMOVE_CONNECTION'; payload: { sourceId: string; destinationId: string } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_SELECTED_CROSSPOINT'; payload: { sourceId: string; destinationId: string } | null }
  | { type: 'SET_HOVERED_CROSSPOINT'; payload: { sourceId: string; destinationId: string } | null }
  | { type: 'SET_VIEW_MODE'; payload: '2d' | '3d' }
  | { type: 'UPDATE_PERFORMANCE'; payload: { frameRate?: number; responseTime?: number } }
  | { type: 'SET_CONNECTION_STATUS'; payload: 'connected' | 'disconnected' | 'connecting' }
  | { type: 'SET_LAST_UPDATE'; payload: Date }
  | { type: 'INCREMENT_RETRY_COUNT' }
  | { type: 'RESET_RETRY_COUNT' }
  | { type: 'SET_SHIFT_HELD'; payload: boolean }
  | { type: 'TOGGLE_SALVO_CROSSPOINT'; payload: { sourceId: string; destinationId: string } }
  | { type: 'CLEAR_SALVO_SELECTION' }
  | { type: 'PUSH_UNDO_ENTRY'; payload: UndoEntry }
  | { type: 'REMOVE_UNDO_ENTRY'; payload: string }
  | { type: 'PRUNE_EXPIRED_UNDO_ENTRIES'; payload: number /* nowMs */ }
  | { type: 'CLEAR_UNDO_BUFFER' }

// Initial state
const initialState: AppState = {
  routing: {
    sources: [],
    destinations: [],
    connections: [],
    loading: false,
    error: null
  },
  ui: {
    selectedCrosspoint: null,
    hoveredCrosspoint: null,
    viewMode: '2d',
    performance: {
      frameRate: 60,
      responseTime: 0
    },
    shiftHeld: false,
    salvoSelection: [],
    undoBuffer: []
  },
  connection: {
    status: 'disconnected',
    lastUpdate: null,
    retryCount: 0
  }
}

// Reducer function
function routingReducer(state: AppState, action: RoutingAction): AppState {
  switch (action.type) {
    case 'SET_SOURCES':
      return {
        ...state,
        routing: { ...state.routing, sources: action.payload }
      }
    
    case 'SET_DESTINATIONS':
      return {
        ...state,
        routing: { ...state.routing, destinations: action.payload }
      }
    
    case 'SET_CONNECTIONS':
      return {
        ...state,
        routing: { ...state.routing, connections: action.payload }
      }
    
    case 'ADD_CONNECTION':
      return {
        ...state,
        routing: {
          ...state.routing,
          connections: [...state.routing.connections, action.payload]
        }
      }
    
    case 'UPDATE_CONNECTION':
      return {
        ...state,
        routing: {
          ...state.routing,
          connections: state.routing.connections.map(conn =>
            conn.sourceId === action.payload.sourceId && 
            conn.destinationId === action.payload.destinationId
              ? action.payload
              : conn
          )
        }
      }
    
    case 'REMOVE_CONNECTION':
      return {
        ...state,
        routing: {
          ...state.routing,
          connections: state.routing.connections.filter(conn =>
            !(conn.sourceId === action.payload.sourceId && 
              conn.destinationId === action.payload.destinationId)
          )
        }
      }
    
    case 'SET_LOADING':
      return {
        ...state,
        routing: { ...state.routing, loading: action.payload }
      }
    
    case 'SET_ERROR':
      return {
        ...state,
        routing: { ...state.routing, error: action.payload }
      }
    
    case 'SET_SELECTED_CROSSPOINT':
      return {
        ...state,
        ui: { ...state.ui, selectedCrosspoint: action.payload }
      }
    
    case 'SET_HOVERED_CROSSPOINT':
      return {
        ...state,
        ui: { ...state.ui, hoveredCrosspoint: action.payload }
      }
    
    case 'SET_VIEW_MODE':
      return {
        ...state,
        ui: { ...state.ui, viewMode: action.payload }
      }
    
    case 'UPDATE_PERFORMANCE':
      return {
        ...state,
        ui: {
          ...state.ui,
          performance: { ...state.ui.performance, ...action.payload }
        }
      }
    
    case 'SET_CONNECTION_STATUS':
      return {
        ...state,
        connection: { ...state.connection, status: action.payload }
      }
    
    case 'SET_LAST_UPDATE':
      return {
        ...state,
        connection: { ...state.connection, lastUpdate: action.payload }
      }
    
    case 'INCREMENT_RETRY_COUNT':
      return {
        ...state,
        connection: { ...state.connection, retryCount: state.connection.retryCount + 1 }
      }
    
    case 'RESET_RETRY_COUNT':
      return {
        ...state,
        connection: { ...state.connection, retryCount: 0 }
      }

    case 'SET_SHIFT_HELD':
      return { ...state, ui: { ...state.ui, shiftHeld: action.payload } }

    case 'TOGGLE_SALVO_CROSSPOINT': {
      const { sourceId, destinationId } = action.payload
      const existing = state.ui.salvoSelection.findIndex(
        c => c.sourceId === sourceId && c.destinationId === destinationId
      )
      const next = existing >= 0
        ? state.ui.salvoSelection.filter((_, i) => i !== existing)
        : [...state.ui.salvoSelection, { sourceId, destinationId }]
      return { ...state, ui: { ...state.ui, salvoSelection: next } }
    }

    case 'CLEAR_SALVO_SELECTION':
      return { ...state, ui: { ...state.ui, salvoSelection: [] } }

    case 'PUSH_UNDO_ENTRY':
      return {
        ...state,
        ui: {
          ...state.ui,
          // Prune any already-expired entries in the same step so the buffer
          // stays tight even if no prune tick has run yet.
          undoBuffer: [
            ...state.ui.undoBuffer.filter(e => e.expiresAt > action.payload.createdAt),
            action.payload,
          ],
        },
      }

    case 'REMOVE_UNDO_ENTRY':
      return {
        ...state,
        ui: {
          ...state.ui,
          undoBuffer: state.ui.undoBuffer.filter(e => e.id !== action.payload),
        },
      }

    case 'PRUNE_EXPIRED_UNDO_ENTRIES':
      return {
        ...state,
        ui: {
          ...state.ui,
          undoBuffer: state.ui.undoBuffer.filter(e => e.expiresAt > action.payload),
        },
      }

    case 'CLEAR_UNDO_BUFFER':
      return { ...state, ui: { ...state.ui, undoBuffer: [] } }

    default:
      return state
  }
}

// Context
interface RoutingContextType {
  state: AppState
  dispatch: React.Dispatch<RoutingAction>
}

const RoutingContext = createContext<RoutingContextType | undefined>(undefined)

// Provider component
interface RoutingProviderProps {
  children: ReactNode
}

export function RoutingProvider({ children }: RoutingProviderProps) {
  const [state, dispatch] = useReducer(routingReducer, initialState)

  // Track Shift key globally so salvo-build mode can intercept crosspoint
  // clicks without plumbing the MouseEvent through every component.
  useEffect(() => {
    const down = (e: KeyboardEvent) => { if (e.key === 'Shift') dispatch({ type: 'SET_SHIFT_HELD', payload: true }) }
    const up = (e: KeyboardEvent) => { if (e.key === 'Shift') dispatch({ type: 'SET_SHIFT_HELD', payload: false }) }
    const blur = () => dispatch({ type: 'SET_SHIFT_HELD', payload: false })
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    window.addEventListener('blur', blur)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
      window.removeEventListener('blur', blur)
    }
  }, [])

  // Prune expired undo entries every second. Cheap; the buffer is O(few) items.
  useEffect(() => {
    const handle = setInterval(() => {
      dispatch({ type: 'PRUNE_EXPIRED_UNDO_ENTRIES', payload: Date.now() })
    }, 1000)
    return () => clearInterval(handle)
  }, [])

  return (
    <RoutingContext.Provider value={{ state, dispatch }}>
      {children}
    </RoutingContext.Provider>
  )
}

// Custom hook to use the routing context
export function useRouting() {
  const context = useContext(RoutingContext)
  if (context === undefined) {
    throw new Error('useRouting must be used within a RoutingProvider')
  }
  return context
}

// Action creators for common operations
export const routingActions = {
  setSources: (sources: Source[]) => ({ type: 'SET_SOURCES' as const, payload: sources }),
  setDestinations: (destinations: Destination[]) => ({ type: 'SET_DESTINATIONS' as const, payload: destinations }),
  setConnections: (connections: Connection[]) => ({ type: 'SET_CONNECTIONS' as const, payload: connections }),
  addConnection: (connection: Connection) => ({ type: 'ADD_CONNECTION' as const, payload: connection }),
  updateConnection: (connection: Connection) => ({ type: 'UPDATE_CONNECTION' as const, payload: connection }),
  removeConnection: (sourceId: string, destinationId: string) => 
    ({ type: 'REMOVE_CONNECTION' as const, payload: { sourceId, destinationId } }),
  setLoading: (loading: boolean) => ({ type: 'SET_LOADING' as const, payload: loading }),
  setError: (error: string | null) => ({ type: 'SET_ERROR' as const, payload: error }),
  setSelectedCrosspoint: (crosspoint: { sourceId: string; destinationId: string } | null) => 
    ({ type: 'SET_SELECTED_CROSSPOINT' as const, payload: crosspoint }),
  setHoveredCrosspoint: (crosspoint: { sourceId: string; destinationId: string } | null) => 
    ({ type: 'SET_HOVERED_CROSSPOINT' as const, payload: crosspoint }),
  setViewMode: (mode: '2d' | '3d') => ({ type: 'SET_VIEW_MODE' as const, payload: mode }),
  updatePerformance: (metrics: { frameRate?: number; responseTime?: number }) => 
    ({ type: 'UPDATE_PERFORMANCE' as const, payload: metrics }),
  setConnectionStatus: (status: 'connected' | 'disconnected' | 'connecting') => 
    ({ type: 'SET_CONNECTION_STATUS' as const, payload: status }),
  setLastUpdate: (date: Date) => ({ type: 'SET_LAST_UPDATE' as const, payload: date }),
  incrementRetryCount: () => ({ type: 'INCREMENT_RETRY_COUNT' as const }),
  resetRetryCount: () => ({ type: 'RESET_RETRY_COUNT' as const }),
  setShiftHeld: (held: boolean) => ({ type: 'SET_SHIFT_HELD' as const, payload: held }),
  toggleSalvoCrosspoint: (sourceId: string, destinationId: string) =>
    ({ type: 'TOGGLE_SALVO_CROSSPOINT' as const, payload: { sourceId, destinationId } }),
  clearSalvoSelection: () => ({ type: 'CLEAR_SALVO_SELECTION' as const }),
  pushUndoEntry: (entry: UndoEntry) => ({ type: 'PUSH_UNDO_ENTRY' as const, payload: entry }),
  removeUndoEntry: (id: string) => ({ type: 'REMOVE_UNDO_ENTRY' as const, payload: id }),
  pruneExpiredUndoEntries: (nowMs: number) =>
    ({ type: 'PRUNE_EXPIRED_UNDO_ENTRIES' as const, payload: nowMs }),
  clearUndoBuffer: () => ({ type: 'CLEAR_UNDO_BUFFER' as const })
}